import { createContext, useContext, useEffect, useState } from "react";
import {
  PoContractsUpdateFlowContextData,
  PoContractsUpdateFlowStep,
  DefaultPoContractsUpdateFlowStep,
  UpsellPoContractsUpdateFlowStep,
  ProcessingStatus,
  FlowType,
  FlowTransitions,
  SimulateUpsellProps,
  SimulatedUpsellResponse,
  PoContractUpdateData,
  PoInitialContractData,
  PoContractsUpdateFlowStatus,
  ErrorMessages,
  UpsellData,
  SimulatedUpdate,
} from "./use-po-contracts-update-flow.types";
import { useBlocker } from "react-router-dom";
import {
  simulatedPayment,
  simulatedUpdatedCycles,
  SimulatedUpsell,
} from "../use-booking-flow/use-booking-flow.types";
import {
  PackageDetails,
  PackageVersion,
} from "../../layouts/booking-create-page/booking-create-page.types";
import { apiWobaBackend } from "@/services/api-woba-backend";
import { getAlteredUpdateFields } from "./utils/compare-update-objects";

const PoContractsUpdateFlowContext =
  createContext<PoContractsUpdateFlowContextData>(
    {} as PoContractsUpdateFlowContextData,
  );

export function PoContractsUpdateFlowProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentStep, setCurrentStep] = useState<PoContractsUpdateFlowStep>(
    DefaultPoContractsUpdateFlowStep.UPDATE,
  );
  const [poContractUpdateData, setPoContractUpdateData] =
    useState<PoContractUpdateData | null>(null);
  const [poInitialContractData, setPoInitialContractData] =
    useState<PoInitialContractData | null>(null);
  const [flowType, setFlowType] = useState<FlowType>("default");
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>({
    isProcessing: false,
    canNavigateAway: true,
    currentOperation: null,
  });
  const [upsellData, setUpsellData] = useState<UpsellData | null>(null);
  const [simulatedUpsell, setSimulatedUpsell] =
    useState<SimulatedUpsell | null>(upsellData?.simulatedUpsellData ?? null);
  const [simulatedUpdate, setSimulatedUpdate] = useState<SimulatedUpdate>({
    toCreate: {
      count: 0,
      hasProRata: false,
    },
    toUpdate: {
      count: 0,
    },
    toCancel: {
      count: 0,
    },
  });

  const [selectedCycle, setSelectedCycle] = useState<PackageVersion | null>(
    null,
  );
  const [selectedPackage, setSelectedPackage] = useState<PackageDetails | null>(
    null,
  );
  const [selectedPackageGroups, setSelectedPackageGroups] = useState<
    {
      id: number;
      name: string;
    }[]
  >([]);
  const [poContractsUpdateFlowStatus, setPoContractsUpdateFlowStatus] =
    useState<PoContractsUpdateFlowStatus>({
      update: null,
      upsell: null,
    });

  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({
    update: null,
    upsell: null,
  });

  const updatePoContractsUpdateFlowStatus = (
    status: Partial<PoContractsUpdateFlowStatus>,
  ) => {
    setPoContractsUpdateFlowStatus((prev) => ({ ...prev, ...status }));
  };

  const UpdateUpsellPayments = (payments: simulatedPayment[]) => {
    setSimulatedUpsell((prev) =>
      prev ? { ...prev, newPayments: payments } : prev,
    );
  };

  const UpdateUpsellCycles = (cycles: simulatedUpdatedCycles[]) => {
    setSimulatedUpsell((prev) =>
      prev ? { ...prev, updatedCycles: cycles } : prev,
    );
  };

  const SimulateUpsell = async ({
    creditAmount,
    beginDate,
    companyId,
    packageId,
  }: SimulateUpsellProps) => {
    const response: SimulatedUpsellResponse = await apiWobaBackend.post(
      `/companies/${companyId}/packages/${packageId}/simulate-upsell`,
      {
        creditAmount,
        beginDate,
      },
    );

    setSimulatedUpsell(response.data);
  };

  const SimulateUpdate = async () => {
    try {
      const { alteredFields } = getAlteredUpdateFields(
        poContractUpdateData,
        poInitialContractData,
      );

      const response: { data: SimulatedUpdate } = await apiWobaBackend.post(
        `/private-offices/contracts/${poContractUpdateData?.id}/preview`,
        alteredFields,
      );

      setSimulatedUpdate(response.data);
    } catch (error) {
      setSimulatedUpdate({
        toCreate: {
          count: 0,
          hasProRata: false,
        },
        toUpdate: {
          count: 0,
        },
        toCancel: {
          count: 0,
        },
      });
    }
  };

  const resetFlow = () => {
    setFlowType("default");
    setPoContractUpdateData(null);
    setPoInitialContractData(null);
    setSelectedCycle(null);
    setSelectedPackage(null);
    setSelectedPackageGroups([]);
    setPoContractsUpdateFlowStatus({
      update: null,
      upsell: null,
    });
    setErrorMessages({ update: null, upsell: null });
    setCurrentStep(DefaultPoContractsUpdateFlowStep.UPDATE);
  };

  const moveToSummaryStep = () => {
    const nextStep =
      flowType === "default"
        ? DefaultPoContractsUpdateFlowStep.SUMMARY
        : UpsellPoContractsUpdateFlowStep.SUMMARY;
    setCurrentStep(nextStep);
  };

  const nextStep = () => {
    const transition = FlowTransitions[currentStep];
    setCurrentStep(transition.next);
  };

  const previousStep = () => {
    const transition = FlowTransitions[currentStep];
    if (transition.previous) {
      setCurrentStep(transition.previous);
    }
  };

  const handleSubmitPoContractUpdate = (data: PoContractUpdateData) => {
    setPoContractUpdateData(data);
    setFlowType(data.flowType);
    const isUpsell = data.flowType === "upsell";
    const nextStep = isUpsell
      ? UpsellPoContractsUpdateFlowStep.UPSELL
      : DefaultPoContractsUpdateFlowStep.SUMMARY;
    setCurrentStep(nextStep);
  };

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      processingStatus.isProcessing &&
      !processingStatus.canNavigateAway &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  // blocker to prevent navigation away from the page while processing
  useEffect(() => {
    if (blocker.state === "blocked") {
      const shouldNavigate = window.confirm(
        "Operação em andamento. Deseja realmente sair?",
      );
      if (shouldNavigate) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  // blocker to prevent external navigation while processing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (processingStatus.isProcessing && !processingStatus.canNavigateAway) {
        e.preventDefault();
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [processingStatus]);

  return (
    <PoContractsUpdateFlowContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        nextStep,
        handleSubmitPoContractUpdate,
        poContractUpdateData,
        poInitialContractData,
        setPoInitialContractData,
        SimulateUpsell,
        simulatedUpsell,
        UpdateUpsellPayments,
        UpdateUpsellCycles,
        upsellData,
        setUpsellData,
        previousStep,
        flowType,
        setSelectedCycle,
        selectedCycle,
        setSelectedPackage,
        selectedPackage,
        setSelectedPackageGroups,
        selectedPackageGroups,
        poContractsUpdateFlowStatus,
        setPoContractsUpdateFlowStatus: updatePoContractsUpdateFlowStatus,
        errorMessages,
        setErrorMessages,
        processingStatus,
        setProcessingStatus,
        resetFlow,
        moveToSummaryStep,
        SimulateUpdate,
        simulatedUpdate,
      }}
    >
      {children}
    </PoContractsUpdateFlowContext.Provider>
  );
}

export const usePoContractsUpdateFlow = () => {
  const context = useContext(PoContractsUpdateFlowContext);
  if (!context) {
    throw new Error(
      "usePoContractsUpdateFlow must be used within a PoContractsUpdateFlowProvider",
    );
  }
  return context;
};
